import { useEffect, useRef } from 'react';

type useImpressionCallback = (entry: IntersectionObserverEntry) => void;

const defaultObserverOptions = {
  root: null,
  rootMargin: '0px',
  threshold: 0,
};

export const useImpressionRef = <T extends HTMLElement>(
  callback: useImpressionCallback,
  options: IntersectionObserverInit = defaultObserverOptions,
) => {
  const ref = useRef<T | null>(null);
  const hasIntersected = useRef(false);

  useEffect(() => {
    if (hasIntersected.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasIntersected.current) {
            hasIntersected.current = true;
            callback(entry);

            if (ref.current) {
              observer.unobserve(ref.current);
            }
          }
        });
      },
      { ...defaultObserverOptions, ...options },
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [callback, options]);

  return ref;
};
